import "./environment.d";
import "@nim-os/in2-app-talk";
import DateProvider from "@ls/date-module";
import { DistributionProvider } from "@ls/distribution";
import { EntityMappingServiceProvider } from "@ls/entity-mapping-service";
import { EntityServiceProvider } from "@ls/entity-service";
import { ErrorBoundary } from "@ls/error-module";
import { GroupingServiceProvider } from "@ls/grouping-service";
import { ThemeProvider } from "@ls/ls-style";
import { PersistenceProvider } from "@ls/persistence-module";
import { PromptProvider } from "@ls/react-prompt";
import { Provider as ToastProvider } from "@ls/react-toast";
import React from "react";
import { render } from "react-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { Provider as StoreProvider } from "react-redux";
import { BrowserRouter as Router } from "react-router-dom";
import store from "state/store";
import { UserProvider, LegacyAuthProvider } from "@ls/user-module";
import App from "./App";
import { setFetcher } from "@ls/js-fetch";

console.info("IN2_ENV:", process.env.REACT_APP_IN2_ENV);
console.info("UI_VERSION:", process.env.REACT_APP_UI_VERSION);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 60 * 2, //ms * second * min * hours
    },
  },
});
const appId = process.env.REACT_APP_ID;
if (
  process.env.REACT_APP_HOST_ENV === "AZURE" &&
  !process.env.REACT_APP_ALLOWED_TENANT_IDS
) {
  throw new Error("REACT_APP_ALLOWED_TENANT_IDS is not defined");
}

const allowedTenantIds = process.env.REACT_APP_ALLOWED_TENANT_IDS?.split(
  ",",
).map((tid) => tid.trim());

const hostEnv = process.env.REACT_APP_HOST_ENV;
console.log("process.env.REACT_APP_HOST_ENV", hostEnv);

// Initialize the app after MSAL is ready
const initializeApp = async () => {
  console.log("Starting app...");
  let Auth;
  if (process.env.REACT_APP_HOST_ENV === "AZURE") {
    Auth = function Auth(props) {
      return (
        <UserProvider
          allowedTenantIds={allowedTenantIds}
          setFetcher={setFetcher}
        >
          {props.children}
        </UserProvider>
      );
    };
  } else {
    Auth = function Auth(props) {
      return <LegacyAuthProvider>{props.children}</LegacyAuthProvider>;
    };
  }

  const renderApp = () => {
    render(
      <Auth>
        <DistributionProvider>
          <StoreProvider store={store}>
            <ErrorBoundary>
              <ThemeProvider>
                <EntityServiceProvider
                  appId={appId}
                  baseURL={process.env.REACT_APP_BASE_URL_ENTITY_SERVICE}
                >
                  <EntityMappingServiceProvider
                    appId={appId}
                    baseURL={process.env.REACT_APP_BASE_URL_ENTITY_MAPPING}
                  >
                    <GroupingServiceProvider
                      appId={appId}
                      baseURL={process.env.REACT_APP_BASE_URL_GROUPING_SERVICE}
                    >
                      <PersistenceProvider appId={process.env.REACT_APP_ID}>
                        <DateProvider
                          appId={appId}
                          baseURL={
                            process.env.REACT_APP_BASE_URL_ENTITY_SERVICE
                          }
                        >
                          <PromptProvider>
                            <ToastProvider>
                              <Router>
                                <QueryClientProvider client={queryClient}>
                                  <App />
                                </QueryClientProvider>
                              </Router>
                            </ToastProvider>
                          </PromptProvider>
                        </DateProvider>
                      </PersistenceProvider>
                    </GroupingServiceProvider>
                  </EntityMappingServiceProvider>
                </EntityServiceProvider>
              </ThemeProvider>
            </ErrorBoundary>
          </StoreProvider>
        </DistributionProvider>
      </Auth>,
      document.getElementById("root"),
    );
  };

  await renderApp();
};

// Initialize the app
initializeApp().catch(console.error);

if (process.env.REACT_APP_IN2_ENV !== "PRD") {
  const el = document.createElement("span");
  el.appendChild(document.createTextNode(process.env.REACT_APP_IN2_ENV));
  el.setAttribute(
    "style",
    "color:#f0f71b;position:fixed;z-index:100;top:2px;left:2px;font-size:1.6em;font-weight:bold;",
  );
  el.setAttribute("id", "env-warn");
  document.body.insertBefore(el, document.getElementById("root"));
}
